// src/components/notification/NotificationListView.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
    NotificationConnection,
    Notification,
    User,
    useNotificationsQuery,
    useMarkNotificationsSeenMutation,
    SeenState
} from "../../gql/types";
import styles from "./NotificationListView.module.css";
import NotificationList from "./NotificationList";

interface NotificationListViewProps {
    user?: User;
    onClickNotification?: (notification: Notification) => void;
}

const NotificationListView: React.FC<NotificationListViewProps> = ({
    user,
    onClickNotification,
}) => {
    const [updateSeenStatus] = useMarkNotificationsSeenMutation();
    const { data, loading: initialLoading, fetchMore } = useNotificationsQuery({
        variables: {
            seenState: null,
            limit: 15,
            cursor: null,
        },
        skip: !user,
        fetchPolicy: "cache-and-network",
    });

    const [connection, setConnection] = useState<NotificationConnection>({
        edges: [],
        pageInfo: { hasNextPage: false, endCursor: null },
    });
    const [loadingMore, setLoadingMore] = useState(false);

    //TODO: Handle notification click properly with onClickNotification
    const handleNotificationClick = (notification: Notification) => {
        onClickNotification?.(notification);
    };

    //1. Initialize local connection when first data loads
    useEffect(() => {
        if (data?.notifications?.edges.length) {
            setConnection(data.notifications);
        }
    }, [data]);

    //2. loadMore using fetchMore
    const loadMore = useCallback(async (cursor?: string | null) => {
        if (!connection.pageInfo?.hasNextPage || loadingMore || !user) return;

        setLoadingMore(true);
        try {
            const { data: moreData } = await fetchMore({
                variables: {
                    seenState: null,
                    limit: 10,
                    cursor: cursor ?? connection.pageInfo.endCursor,
                },
            });

            if (moreData?.notifications?.edges.length) {
                setConnection((prev) => ({
                    __typename: "NotificationConnection",
                    edges: [...prev.edges, ...moreData.notifications.edges],
                    pageInfo: moreData.notifications.pageInfo,
                }));
            } else {
                setConnection((prev) => ({
                    ...prev,
                    pageInfo: {
                        hasNextPage: false,
                        endCursor: null,
                    },
                }));
            }
        } catch (err) {
            console.error("Pagination failed", err);
        } finally {
            setLoadingMore(false);
        }
    }, [connection.pageInfo, loadingMore, fetchMore, user]);

    //TODO: Handle notification load leading to mark as seen behavior

    //Mark unseen notifications as seen when connection edges is updated
    useEffect(() => {
        const unseenNotificationIDs = connection.edges
            .map(edge => edge.node)
            .filter(notification => notification.seenState === SeenState.Unseen)
            .map(notification => notification.id);

        if (unseenNotificationIDs.length > 0) {
            updateSeenStatus({
                variables: {
                    notificationIDs: unseenNotificationIDs,
                },
            }).catch((err) => {
                console.error("Failed to mark notifications as seen", err);
            });
        }
    }, [connection.edges]);

    return (
        <div className={styles.notificationListViewContainer}>
            <NotificationList
                connection={connection}
                loading={initialLoading || loadingMore}
                isPaginated={true}
                loadMore={loadMore}
                onClickNotification={handleNotificationClick}
            />
        </div>
    );
};

export default NotificationListView;